const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';
const ZOOM_IN_SCALE_AMOUNT = 0.2 / 100;

let zoomAnimationObserver;
let zoomAnimationHandlerAttached = false;
const visibleZoomAnimationElements = new Set();
let scrollAnimationObserver;

// Scroll in animation logic
function onIntersection(elements, observer) {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (prefersReducedMotion) {
        // Immediately remove offscreen class without animation
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        elementTarget.classList.add(SCROLL_ANIMATION_CANCEL_CLASSNAME);
      } else if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Reuse the existing observer to avoid leaking observers in the Shopify editor
  if (!scrollAnimationObserver) {
    scrollAnimationObserver = new IntersectionObserver(onIntersection, {
      rootMargin: '0px 0px -50px 0px',
    });
  }
  animationTriggerElements.forEach((element) => {
    if (prefersReducedMotion) {
      element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.classList.add(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    } else {
      scrollAnimationObserver.observe(element);
    }
  });
}

function updateZoomInRatio(elements = visibleZoomAnimationElements) {
  if (elements.size === 0) return;

  // Batch all reads before any writes to avoid layout thrashing
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const updates = [];

  elements.forEach((element) => {
    const rect = element.getBoundingClientRect();
    const elementPositionY = rect.top + scrollY;
    const elementHeight = element.offsetHeight;
    const ratio = 1 + ZOOM_IN_SCALE_AMOUNT * calculatePercentageSeen(elementPositionY, elementHeight, viewportHeight, scrollY);
    updates.push({ element, ratio });
  });

  // Now batch all writes
  updates.forEach(({ element, ratio }) => {
    element.style.setProperty('--zoom-in-ratio', ratio);
  });
}

function onZoomAnimationIntersection(entries) {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      visibleZoomAnimationElements.add(entry.target);
    } else {
      visibleZoomAnimationElements.delete(entry.target);
    }
  });

  if (visibleZoomAnimationElements.size > 0) {
    updateZoomInRatio();
  }
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger(rootEl = document) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME)).filter(
    (element) => !element.hasAttribute('data-zoom-initialized')
  );

  if (animationTriggerElements.length === 0) return;

  if (!zoomAnimationObserver) {
    zoomAnimationObserver = new IntersectionObserver(onZoomAnimationIntersection);
  }

  animationTriggerElements.forEach((element) => {
    element.setAttribute('data-zoom-initialized', 'true');
    element.style.setProperty('--zoom-in-ratio', 1 + ZOOM_IN_SCALE_AMOUNT * percentageSeen(element));
    zoomAnimationObserver.observe(element);
  });

  if (!zoomAnimationHandlerAttached) {
    const handleZoomAnimation = throttle(() => {
      if (visibleZoomAnimationElements.size === 0) return;

      window.requestAnimationFrame(() => {
        updateZoomInRatio();
      });
    }, 50);

    window.addEventListener('scroll', handleZoomAnimation, { passive: true });
    window.addEventListener('resize', handleZoomAnimation, { passive: true });
    zoomAnimationHandlerAttached = true;
  }
}

function calculatePercentageSeen(elementPositionY, elementHeight, viewportHeight, scrollY) {
  if (elementPositionY > scrollY + viewportHeight) {
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    return 100;
  }

  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  return calculatePercentageSeen(elementPositionY, elementHeight, viewportHeight, scrollY);
}

function initializeAnimationsWhenIdle() {
  const initialize = () => {
    initializeScrollAnimationTrigger();
    initializeScrollZoomAnimationTrigger();
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(initialize, { timeout: 1200 });
  } else {
    window.setTimeout(initialize, 250);
  }
}

if (document.readyState === 'loading') {
  window.addEventListener('DOMContentLoaded', initializeAnimationsWhenIdle, { once: true });
} else {
  initializeAnimationsWhenIdle();
}

if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initializeScrollAnimationTrigger(event.target, true);
    initializeScrollZoomAnimationTrigger(event.target);
  });
  document.addEventListener('shopify:section:reorder', () => {
    initializeScrollAnimationTrigger(document, true);
    initializeScrollZoomAnimationTrigger(document);
  });
}
